import { Component, OnInit } from '@angular/core';
import { NzMessageService } from 'ng-zorro-antd/message';
import { AppService } from '../../../shared/services/app.service';
import { UserManagementService } from '../../../shared/services/user-management.service';
import { ApiService } from '../../../shared/services/api.service';
import {
  GetResourcesQuery,
  GetResourcesQueryVariables,
  GetPreSignedUrlQuery,
  GetPreSignedUrlQueryVariables,
  PreSignedUrl,
  ResourceQueryInput,
  Resource,
  Section,
} from '../../../../API';
import { GetResources, GetPreSignedUrl } from '../../../../graphql/queries';
import { FAQ, MANUAL, REPORT } from '../../models/resources';

@Component({
  selector: 'app-resources',
  templateUrl: './resources.component.html',
  styleUrl: './resources.component.scss',
})
export class ResourcesComponent implements OnInit {
  isLoading = false;
  resourceData: ResourceQueryInput | null = null;
  orgId!: string;
  sectionName!: Section;
  faqs: FAQ[] = [];
  manuals: MANUAL[] = [];
  reports: REPORT[] = [];

  tabs: string[] = ['Manuals', 'Reports', 'FAQs'];
  selectedTitle = 'Manuals';

  constructor(
    private apiService: ApiService,
    private userManagementService: UserManagementService,
    private nzMessageService: NzMessageService,
    private appService: AppService,
  ) {
    this.orgId = this.userManagementService.getOrgId();
  }

  ngOnInit(): void {
    this.getAllResources();
  }

  onTabChange(index: number) {
    this.selectedTitle = this.tabs[index];
    this.getAllResources();
  }

  getAllResources(): void {
    this.isLoading = true;
    let section = Section.MANUAL;

    switch (this.selectedTitle) {
      case 'Manuals':
        section = Section.MANUAL;
        break;
      case 'Reports':
        section = Section.REPORT;
        break;
      case 'FAQs':
        section = Section.FAQ;
        break;
      default:
        console.log('Invalid tab selection');
    }
    this.sectionName = section;

    const resourceQueryInput: ResourceQueryInput = {
      orgId: this.orgId,
      section: this.sectionName,
    };
    this.apiService
      .query<GetResourcesQuery, GetResourcesQueryVariables>(GetResources, {
        input: resourceQueryInput,
      })
      .then((response) => {
        const resources = (response.data?.GetResources ?? []).filter((r): r is Resource => r !== null);
        switch (this.selectedTitle) {
          case 'Manuals':
            this.mapManuals(resources);
            break;
          case 'Reports':
            this.mapReports(resources);
            break;
          case 'FAQs':
            this.mapFaqs(resources);
            break;
          default:
            console.log('Invalid selection');
        }
      })
      .catch((error) => {
        this.nzMessageService.error(`Failed to load resources ${error}`);
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  mapFaqs(resources: any[]) {
    const unique = Array.from(new Map(resources.map((item) => [item.title, item])).values());

    this.faqs = unique.map((item) => ({
      question: item.title,
      answer: item.description,
      active: false,
    }));
  }

  mapManuals(resources: any[]) {
    const unique = Array.from(new Map(resources.map((item) => [item.title, item])).values());

    this.manuals = unique.map((item) => ({
      title: item.title,
      description: item.description,
      fileUrl: item.fileUrl,
    }));
  }

  mapReports(resources: any[]) {
    const grouped: Record<string, any[]> = {};
    resources.forEach((item) => {
      if (!grouped[item.groupId]) {
        grouped[item.groupId] = [];
      }
      grouped[item.groupId].push({
        name: item.title,
        size: item.description.replace(/[()]/g, ''),
        fileUrl: item.fileUrl,
      });
    });

    this.reports = Object.keys(grouped).map((key) => ({
      groupName: key,
      reports: grouped[key],
      active: false,
    }));
  }

  async downloadResource(docUrl: string): Promise<void> {
    this.isLoading = true;
    const s3key = this.extractS3Key(docUrl);
    const input: PreSignedUrl = {
      contentType: '',
      bucketSuffix: 'resource-bucket',
      s3keyName: s3key,
    };

    try {
      const { data } = await this.apiService.query<GetPreSignedUrlQuery, GetPreSignedUrlQueryVariables>(
        GetPreSignedUrl,
        { input },
      );

      const preSignedUrlResponse = data?.GetPreSignedUrl;
      if (!preSignedUrlResponse || preSignedUrlResponse.statusCode !== 200 || !preSignedUrlResponse.getUrl) {
        this.nzMessageService.error('Failed to get pre-signed URL, please try again');
        return;
      }
      if (preSignedUrlResponse.getUrl) {
        this.triggerDownload(preSignedUrlResponse.getUrl);
      } else {
        console.error('No presigned URL received');
      }
    } catch (error: unknown) {
      this.nzMessageService.error((error as Error)?.message ?? 'Unexpected error while getting presigned url.');
      console.error('Error in uploadAndSaveChart:', error);
    } finally {
      this.isLoading = false;
    }
  }

  private triggerDownload(url: string) {
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.click();
    this.isLoading = false;
  }

  private extractS3Key(url: string): string {
    const urlObj = new URL(url);
    return urlObj.pathname.substring(1);
  }

  contactUs(): void {
    window.open(this.appService.contactUsLink, '_blank');
  }
}
